import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import { campaignsData } from "@/data/platformData";
import { servicesData, serviceBundlesData } from "@/data/mockData";
import {
  Megaphone, Plus, Pencil, Trash2, Calendar, Percent, Tag, ArrowLeft
} from "lucide-react";

const PHARMACY_ID = "ph1";

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  draft: "bg-slate-50 text-slate-700 border-slate-200",
  ended: "bg-zinc-50 text-zinc-600 border-zinc-200"
};

const empty = { name: "", services: [], bundleId: "", discount: 10, period: { start: "", end: "" }, pharmacyIds: [PHARMACY_ID], status: "draft" };

export const ManagerCampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState(
    campaignsData.filter(c => c.pharmacyIds.includes(PHARMACY_ID))
  );
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const startEdit = (c) => {
    setEditing(c ? { ...c, period: { ...c.period }, services: [...c.services] } : { ...empty, id: `camp${Date.now()}` });
    setOpen(true);
  };

  const save = () => {
    if (!editing.name || !editing.period.start || !editing.period.end) {
      toast.error("Compila nome e periodo");
      return;
    }
    if (editing.services.length === 0 && !editing.bundleId) {
      toast.error("Seleziona almeno una prestazione o un profilo");
      return;
    }
    setCampaigns(prev => {
      const exists = prev.find(p => p.id === editing.id);
      if (exists) return prev.map(p => p.id === editing.id ? editing : p);
      return [...prev, editing];
    });
    setOpen(false);
    toast.success(editing.id?.startsWith("camp") && !campaigns.find(p => p.id === editing.id) ? "Campagna creata" : "Campagna aggiornata");
  };

  const remove = (id) => {
    setCampaigns(prev => prev.filter(c => c.id !== id));
    toast.success("Campagna eliminata");
  };

  const toggleService = (sid) => {
    setEditing(e => ({
      ...e,
      services: e.services.includes(sid) ? e.services.filter(x => x !== sid) : [...e.services, sid]
    }));
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/manager")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Megaphone className="h-4 w-4 text-amber-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Marketing</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Campagne promozionali</h1>
            <p className="text-sm text-muted-foreground mt-1">Sconti su prestazioni e profili</p>
          </div>
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="campaigns-new-btn">
            <Plus className="h-4 w-4" /> Nuova campagna
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {campaigns.map(c => {
          const services = c.services.map(sid => servicesData.find(s => s.id === sid)?.name).filter(Boolean);
          const bundle = c.bundleId ? serviceBundlesData.find(b => b.id === c.bundleId) : null;
          return (
            <Card key={c.id} data-testid={`campaign-card-${c.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{c.name}</CardTitle>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge variant="outline" className={statusStyles[c.status]}>{c.status}</Badge>
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {c.period.start} → {c.period.end}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(c)} data-testid={`campaign-edit-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(c.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Percent className="h-4 w-4 text-amber-600" />
                  <span className="font-semibold text-amber-700">-{c.discount}%</span>
                  <span className="text-muted-foreground">di sconto</span>
                </div>
                {bundle && (
                  <div className="text-xs flex items-center gap-2">
                    <Tag className="h-3 w-3 text-indigo-600" />
                    <span className="text-muted-foreground">Profilo:</span>
                    <Badge variant="secondary">{bundle.name}</Badge>
                  </div>
                )}
                {services.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {services.map(s => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {campaigns.length === 0 && (
          <Card className="border-dashed border-2 md:col-span-2">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              Nessuna campagna attiva. Crea la prima per iniziare.
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && campaigns.find(p => p.id === editing.id) ? "Modifica campagna" : "Nuova campagna"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="camp-name">Nome</Label>
                <Input
                  id="camp-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Es. Mese del Cuore"
                  data-testid="campaign-form-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="camp-start">Inizio</Label>
                  <Input
                    id="camp-start"
                    type="date"
                    value={editing.period.start}
                    onChange={(e) => setEditing({ ...editing, period: { ...editing.period, start: e.target.value } })}
                  />
                </div>
                <div>
                  <Label htmlFor="camp-end">Fine</Label>
                  <Input
                    id="camp-end"
                    type="date"
                    value={editing.period.end}
                    onChange={(e) => setEditing({ ...editing, period: { ...editing.period, end: e.target.value } })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="camp-discount">Sconto (%)</Label>
                <Input
                  id="camp-discount"
                  type="number"
                  min="1" max="100"
                  value={editing.discount}
                  onChange={(e) => setEditing({ ...editing, discount: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label>Profilo (bundle)</Label>
                <select
                  className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                  value={editing.bundleId}
                  onChange={(e) => setEditing({ ...editing, bundleId: e.target.value })}
                >
                  <option value="">— Nessuno —</option>
                  {serviceBundlesData.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Prestazioni incluse</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto p-2 rounded-md border">
                  {servicesData.map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={editing.services.includes(s.id)}
                        onCheckedChange={() => toggleService(s.id)}
                      />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Stato</Label>
                <select
                  className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                  value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                >
                  <option value="draft">Bozza</option>
                  <option value="scheduled">Programmata</option>
                  <option value="active">Attiva</option>
                  <option value="ended">Conclusa</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="campaign-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerCampaignsPage;
